import { useEffect, useState } from "react";
import { STARTER, deviceName, fillWithDemo, fillWithStarter, lagersOf, wordFor } from "../account.ts";
import type { Membership, SignedIn } from "../account.ts";
import { store } from "../store.ts";
import { IS_PANTRY, WORDS } from "../brand.ts";
import { AuthPage, messageOf } from "./Login.tsx";

/**
 * After signing in: which workspace (if several) and which storages this device holds. An empty
 * workspace is set up here, with the storages the wine profile suggests or with the demo wine bar.
 */
export function Choose({ signedIn, onDone }: { signedIn: SignedIn; onDone: () => void }) {
  const [workspace, setWorkspace] = useState<Membership | null>(signedIn.workspaces.length === 1 ? signedIn.workspaces[0]! : null);
  const [lagers, setLagers] = useState<{ id: string; name: string }[] | null>(null);
  /** Only an owner sets up an empty workspace; someone else without storages is told so (F20). */
  const [owner, setOwner] = useState(true);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [starter, setStarter] = useState<Set<string>>(new Set(STARTER.map((l) => l.id)));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!workspace) return;
    // Signed in again into the workspace this device holds: keep its data and waiting changes.
    if (store.config && !store.config.token && store.config.workspaceId === workspace.id) {
      void store.updateConfig({ userId: signedIn.user.id });
      store.resume();
      location.hash = IS_PANTRY ? "#/start" : "#/buchen";
      onDone();
      return;
    }
    setLagers(null);
    lagersOf(workspace.id).then(
      (found) => {
        setLagers(found.lagers);
        setOwner(!found.access || found.access.role === "owner");
        setChosen(new Set(found.lagers.map((l) => l.id)));
      },
      (err) => setError(messageOf(err)),
    );
  }, [workspace]);

  const connect = (ws: Membership, ids: string[]) =>
    store.connect({ token: "", workspace: ws.name, workspaceId: ws.id, kind: ws.kind, lagers: ids, device: deviceName(), userId: signedIn.user.id });

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      location.hash = IS_PANTRY ? "#/start" : "#/buchen";
      onDone();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (set: Set<string>, id: string, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(id);
    else next.delete(id);
    return next;
  };

  if (!workspace) {
    return (
      <AuthPage>
        <div className="card">
          <h2>Hallo {signedIn.user.displayName}!</h2>
          {signedIn.workspaces.length === 0 ? (
            <p>Dein Konto gehört noch zu keinem Betrieb.</p>
          ) : (
            <>
              <p>Womit möchtest du auf diesem Gerät arbeiten?</p>
              {signedIn.workspaces.map((w) => (
                <button key={w.id} className="wide" onClick={() => setWorkspace(w)}>
                  {w.name}
                </button>
              ))}
            </>
          )}
        </div>
      </AuthPage>
    );
  }

  const word = wordFor(workspace.kind);
  return (
    <AuthPage>
      <div className="card">
        <h2>{workspace.name}</h2>
        {lagers === null && !error && <p className="muted">Lädt …</p>}
        {lagers && lagers.length === 0 && !owner && (
          <>
            <p data-testid="nothing-granted">Hier ist nichts für dich freigegeben. Welche Lager du siehst und wo du buchen darfst, legt ein Inhaber unter Konto → Betrieb fest.</p>
            <button className="primary wide" disabled={busy} data-testid="connect" onClick={() => run(() => connect(workspace, []))}>
              Weiter
            </button>
            <p className="hint">Sobald ein Lager für dich freigegeben ist, erscheint es von selbst.</p>
          </>
        )}
        {lagers && lagers.length === 0 && owner && (
          <>
            <p>
              {IS_PANTRY
                ? `Willkommen, ${signedIn.user.displayName}! Hier gibt es noch keinen Vorrat. Welche Lager brauchst du?`
                : `Willkommen, ${signedIn.user.displayName}! ${word.one === "Betrieb" ? "Dein Betrieb" : "Dein Keller"} ist noch leer. Welche Lager brauchst du?`}
            </p>
            {STARTER.map((l) => (
              <label key={l.id} className="check">
                <input type="checkbox" checked={starter.has(l.id)} onChange={(e) => setStarter(toggle(starter, l.id, e.target.checked))} />
                <span>
                  {l.name}
                  <br />
                  <span className="muted">{l.locations.map(([, name]) => name).join(", ")}</span>
                </span>
              </label>
            ))}
            <p className="hint">Lagerorte lassen sich später ändern und ergänzen.</p>
            <button
              className="primary wide"
              disabled={busy || starter.size === 0}
              data-testid="starter"
              onClick={() =>
                run(async () => {
                  await connect(workspace, [...starter]);
                  await fillWithStarter([...starter]);
                })
              }
            >
              Einrichten
            </button>
            <div className="or">
              <p className="muted">Erst einmal ansehen?</p>
              <button
                className="wide"
                disabled={busy}
                data-testid="demo"
                onClick={() =>
                  run(async () => {
                    await connect(workspace, STARTER.map((l) => l.id));
                    await fillWithDemo(workspace.kind);
                  })
                }
              >
                {IS_PANTRY ? "Mit dem Demo-Vorrat füllen" : workspace.kind === "private" ? "Mit dem Demo-Keller füllen" : "Mit der Demo-Weinbar füllen"}
              </button>
              <p className="hint">
                {IS_PANTRY
                  ? "Der Vorrat der erfundenen Familie Sommer: 22 Lebensmittel und Haushaltsartikel, einige laufen bald ab, einige sind nachzukaufen. Gut zum Zeigen; nicht für den echten Haushalt."
                  : "Die Weinkarte der erfundenen Weinbar Rebstock: 22 Weine mit Kartennummer und Bestand im Keller. Gut zum Zeigen; nicht für den echten Betrieb."}
              </p>
            </div>
          </>
        )}
        {lagers && lagers.length > 0 && (
          <>
            <p>Welche Lager soll dieses Gerät führen?</p>
            {lagers.map((l) => (
              <label key={l.id} className="check">
                <input type="checkbox" checked={chosen.has(l.id)} onChange={(e) => setChosen(toggle(chosen, l.id, e.target.checked))} />
                {l.name}
              </label>
            ))}
            <p className="hint">{WORDS.device} Was es führt, hat es auch ohne Netz.</p>
            <button className="primary wide" disabled={busy || chosen.size === 0} data-testid="connect" onClick={() => run(() => connect(workspace, [...chosen]))}>
              Los geht's
            </button>
          </>
        )}
        {error && <p className="error-box">{error}</p>}
      </div>
    </AuthPage>
  );
}
